// When it is safe to apply an update that would disturb what the user is doing (issue #3265).
//
// The app has two such updates: reloading the page onto a new bundle (app_update.ts) and reloading
// the current song's files into memory after the cache refresher replaced them (song_reload.ts).
// Both are invisible interruptions when nothing is playing and ruinous in the middle of a
// congregation singing, so both ask here first, each under its own key.
//
// The policy Michael chose in the PRD:
//
//   An update may apply immediately when nothing is playing AND either the current song has not
//   been played since it loaded, or playback has been idle for more than ten minutes. Otherwise it
//   is pending, and applies at the next song transition or once playback has been idle for ten
//   minutes, whichever comes first.
//
// Pause and stop are treated alike - a leader between songs may use either - so "idle" means
// "not playing", however it got there. There is no "tap to refresh" affordance: a camped user
// gets the update when they stop singing.
//
// Holding the page reload is also what makes the service-worker swap fast: Chrome will not
// activate a new worker while the old one has a request in flight, and the player's streaming
// audio is exactly such a request (see the issue thread). By the time the gate opens, the stream
// is closed and the swap takes under two seconds.
//
// The page reload has a second gate on top of that one, because it costs more than a song reload
// does: it also waits until the user has stopped working (no explicit hold is active and nothing
// has been typed or clicked for userIdleMs - see user_activity.ts). Only the apply that passes
// `waitForUserIdle` consults it, so a song whose files changed still refreshes under someone who is
// typing in the Liturgy Planner, while the reload that would throw their cell away waits.
//
// The playback policy is the pure canApplyUpdateNow(), the user policy isAppIdleForReload(), and
// the page reload's combination of the two canReloadAppNow(); runWhenUpdateAllowed() is the thin
// stateful shell around them that holds the pending updates and watches for the gate to open.

import {
  getLastUserActivityAt,
  isAppReloadHeld,
  isEditableElementFocused,
  subscribeAppReloadHolds,
  userIdleMs,
} from '../util/user_activity.ts';
import {
  getPlaybackSnapshot,
  PlaybackSnapshot,
  subscribeIsPlaying,
  subscribeSongTransition,
} from './playback_state.ts';

export const updateIdleMs = 10 * 60_000;

export function canApplyUpdateNow(snapshot: PlaybackSnapshot, now: number): boolean {
  if (snapshot.isPlaying) {
    return false;
  }
  if (!snapshot.hasPlayedCurrentSong) {
    // nothing has been heard from this song yet, so nothing can be interrupted
    return true;
  }
  if (snapshot.lastStoppedAt === undefined) {
    // played, yet never recorded as stopping - no evidence of anything in progress
    return true;
  }
  // inclusive, to agree with the idle timer below, which fires at exactly updateIdleMs and must find
  // the gate open when the loaders ask again
  return now - snapshot.lastStoppedAt >= updateIdleMs;
}

// One slot per key, deliberately: the updates that come through here are "get onto the current
// version", not a queue of distinct actions, so a later request under the same key replaces the
// earlier one rather than running after it. The keys are distinct *kinds* of update - reloading
// the page onto a new bundle ('app-reload') and reloading the current song's files
// ('song-files') - and one must not evict the other, which is why this is a map and not a slot
// (issue #3265 PR C). The gate opening runs every pending apply that is allowed to run, in the
// order they were first requested.
interface PendingApply {
  apply: () => void;
  // the page is going away when this runs, so nothing else pending is worth doing
  exclusive: boolean;
  // also waits for the user to stop working: only the page reload does (issue #3265 PR D)
  waitForUserIdle: boolean;
}

const pendingApplies = new Map<string, PendingApply>();
// counts down the ten minutes of quiet after playback stops
let idleTimer: ReturnType<typeof setTimeout> | undefined;
// counts down the minute of quiet after the user's last pointerdown or keydown
let activityTimer: ReturnType<typeof setTimeout> | undefined;
let unsubscribe: (() => void) | undefined;
let now: () => number = () => Date.now();

// The user half of the gate, which only an apply with waitForUserIdle consults: no explicit hold is
// active, and the user has been quiet for userIdleMs. Never having interacted counts as idle.
export function isAppIdleForReload(now: number): boolean {
  if (isAppReloadHeld()) {
    return false;
  }
  // a focused text field holds whatever is typed in it until blur: the one blur-committed editor
  // nobody has to remember to wire a hold for
  if (isEditableElementFocused()) {
    return false;
  }
  const lastUserActivityAt = getLastUserActivityAt();
  // inclusive, to agree with the activity timer below, which fires at exactly userIdleMs
  return lastUserActivityAt === undefined || now - lastUserActivityAt >= userIdleMs;
}

// Both halves: what the page reload needs before it may take the page away.
export function canReloadAppNow(now: number): boolean {
  return canApplyUpdateNow(getPlaybackSnapshot(), now) && isAppIdleForReload(now);
}

// Runs `apply` immediately when the gate is open for it; otherwise holds it as `key`'s pending
// update and runs it when the gate opens - on a song transition, once playback has been stopped for
// updateIdleMs, and (with waitForUserIdle) once the holds are gone and the user has been quiet for
// userIdleMs.
export function runWhenUpdateAllowed(
  apply: () => void,
  deps: { key: string; exclusive?: boolean; waitForUserIdle?: boolean; now?: () => number },
): void {
  if (deps.now) {
    now = deps.now;
  }
  subscribeToGateInputs();
  // Recorded first either way, so an open gate flushes this request alongside anything that was
  // already pending (which does happen now that one apply can be runnable while another waits).
  pendingApplies.set(deps.key, {
    apply,
    exclusive: deps.exclusive ?? false,
    waitForUserIdle: deps.waitForUserIdle ?? false,
  });
  flushRunnable();
}

// Withdraws `key`'s pending update, so the gate never runs it; true when one was pending. An exclusive
// apply left behind would otherwise still clear every other pending update when the gate opened.
export function cancelUpdate(key: string): boolean {
  if (!pendingApplies.delete(key)) {
    return false;
  }
  if (pendingApplies.size === 0) {
    clearTimers();
  }
  return true;
}

function subscribeToGateInputs(): void {
  if (unsubscribe) {
    return;
  }
  const unsubscribeIsPlaying = subscribeIsPlaying(onIsPlayingChanged);
  const unsubscribeSongTransition = subscribeSongTransition(onSongTransition);
  // a reload waiting on an upload must go the moment the upload finishes, not userIdleMs later
  const unsubscribeHolds = subscribeAppReloadHolds(onHoldsChanged);
  unsubscribe = () => {
    unsubscribeIsPlaying();
    unsubscribeSongTransition();
    unsubscribeHolds();
  };
}

function onIsPlayingChanged(): void {
  if (pendingApplies.size === 0) {
    return;
  }
  if (getPlaybackSnapshot().isPlaying) {
    // singing resumed inside the idle window: the clock starts over when it stops again, and
    // nothing can run meanwhile, so neither timer has anything to wake up for
    clearTimers();
    return;
  }
  // a load failure says the song was never heard: nothing to wait for
  flushRunnable();
}

function onSongTransition(): void {
  // A transition resets hasPlayedCurrentSong, so for a stopped player the playback half is now open
  // and the update applies at once - the PRD's "next song transition", as experienced by someone who
  // stops and skips. It does NOT open while playback carries across the transition (auto-play
  // through a song list, or next/previous while playing): the gate's consumer is a page reload,
  // which would stop the playlist dead, and that is exactly the interruption this rule exists to
  // prevent. Such an update stays pending for the idle window, or for a later transition that lands
  // stopped.
  flushRunnable();
}

function onHoldsChanged(): void {
  // taking a hold (or focusing a field) can only close the user gate; only the opening is worth a look
  if (!isAppIdleForReload(now())) {
    return;
  }
  flushRunnable();
}

// Runs every pending apply the gate currently allows, and re-arms the timers for whatever is left.
function flushRunnable(): void {
  if (pendingApplies.size === 0) {
    clearTimers();
    return;
  }
  const nowMs = now();
  const isPlaybackGateOpen = canApplyUpdateNow(getPlaybackSnapshot(), nowMs);
  const isUserGateOpen = isAppIdleForReload(nowMs);
  const runnable = isPlaybackGateOpen
    ? [...pendingApplies].filter(([, { waitForUserIdle }]) => isUserGateOpen || !waitForUserIdle)
    : [];
  // A page reload makes every other pending update moot, and the song reload in particular would
  // start fetches through the service worker that is about to be replaced, delaying the swap. An
  // exclusive apply that is itself blocked (the reload waiting for the user to stop typing) does
  // not hold the others back: the song's files may refresh under someone editing a song list.
  const exclusive = runnable.filter(([, { exclusive }]) => exclusive);
  const toRun = exclusive.length > 0 ? exclusive : runnable;
  // Taken out before any of them runs, so an apply that asks for another update (a song reload that
  // re-defers because playback started again) lands in the map rather than being removed by this.
  if (exclusive.length > 0) {
    pendingApplies.clear();
  } else {
    for (const [key] of toRun) {
      pendingApplies.delete(key);
    }
  }
  armTimers(isPlaybackGateOpen);
  for (const [, { apply }] of toRun) {
    apply();
  }
}

function armTimers(isPlaybackGateOpen: boolean): void {
  if (isPlaybackGateOpen) {
    // the playback clock has nothing left to wait for; whatever still waits, waits for the user
    clearIdleTimer();
    armActivityTimer();
    return;
  }
  // while playback blocks everything, the idle timer alone wakes the gate
  armIdleTimer();
  clearActivityTimer();
}

function armIdleTimer(): void {
  clearIdleTimer();
  if (pendingApplies.size === 0) {
    return;
  }
  const { isPlaying, lastStoppedAt } = getPlaybackSnapshot();
  if (isPlaying) {
    return;
  }
  const elapsed = lastStoppedAt === undefined ? 0 : now() - lastStoppedAt;
  const remaining = updateIdleMs - elapsed;
  if (remaining <= 0) {
    // the window has already passed: the playback half is open, so there is nothing to wake up for
    // (arming a zero-delay timer here would re-run the gate in a tight loop)
    return;
  }
  idleTimer = setTimeout(onIdleTimerFired, remaining);
}

function onIdleTimerFired(): void {
  idleTimer = undefined;
  if (getPlaybackSnapshot().isPlaying) {
    // belt and braces: onIsPlayingChanged cancels the timer when playback resumes
    return;
  }
  flushRunnable();
}

function armActivityTimer(): void {
  clearActivityTimer();
  const waitsForUser = [...pendingApplies.values()].some(({ waitForUserIdle }) => waitForUserIdle);
  if (!waitsForUser || isAppReloadHeld()) {
    // nothing is watching the activity clock, or a hold is what blocks and its release wakes us
    return;
  }
  const lastUserActivityAt = getLastUserActivityAt();
  if (lastUserActivityAt === undefined) {
    return;
  }
  const remaining = lastUserActivityAt + userIdleMs - now();
  if (remaining <= 0) {
    return;
  }
  // Further activity does not reset this timer; it fires on the old deadline, finds the user still
  // busy, and arms itself again on the new one - so a user who keeps typing keeps pushing the
  // reload out, at the cost of one wakeup per settle window rather than one per keystroke.
  activityTimer = setTimeout(onActivityTimerFired, remaining);
}

function onActivityTimerFired(): void {
  activityTimer = undefined;
  flushRunnable();
}

function cancelPending(): void {
  pendingApplies.clear();
  clearTimers();
}

function clearTimers(): void {
  clearIdleTimer();
  clearActivityTimer();
}

function clearIdleTimer(): void {
  if (idleTimer === undefined) {
    return;
  }
  clearTimeout(idleTimer);
  idleTimer = undefined;
}

function clearActivityTimer(): void {
  if (activityTimer === undefined) {
    return;
  }
  clearTimeout(activityTimer);
  activityTimer = undefined;
}

// test-only: the gate is module state, and its subscriptions outlive a single test
export function resetUpdateGateForTests(): void {
  cancelPending();
  unsubscribe?.();
  unsubscribe = undefined;
  now = () => Date.now();
}
